import { useState, useEffect } from "react";

interface LicensePlate {
  number: string;
  letters: string;
  date: string;
  brand: string;
  model: string;
}

interface DailyPlate {
  date: string;
  number: string;
  letters: string;
  brand: string;
  model: string;
}

export const LastLicensePlate = () => {
  const [lastPlate, setLastPlate] = useState<LicensePlate | null>(null);
  const [dailyPlates, setDailyPlates] = useState<DailyPlate[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch data for the last plate exclusivament del full de càlcul de Google Sheets
  useEffect(() => {
    const cargarUltimaMatricula = async () => {
      try {
        setLoading(true);

        // Intentar agafar la matrícula directament del full de càlcul de Google Sheets.
        // Per evitar problemes de CORS amb la versió HTML, fem servir l'exportació en CSV.
        // La URL és equivalent a la de l'iframe però amb `pub?output=csv`.
        const sheetRes = await fetch(
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vRUFirnDURrk6VUyrpyv2jX5pxEQxnt6GlZiEX5RHcQKQJHX48TI-_SYIeYiyozsPVSs4843_c7v_Rj/pub?output=csv",
          { cache: "no-store" }
        );

        if (!sheetRes.ok) {
          throw new Error("Error al carregar el full de càlcul (CSV)");
        }

        const sheetCsv = await sheetRes.text();

        // Buscar una matrícula dins del CSV.
        // Al full actual apareix com "490* NJK", per tant:
        // - admetem de 1 a 4 dígits
        // - un o més asteriscs
        // - espais opcionals
        // - 3 lletres
        const plateRegex = /(\d{1,4}\*+\s*[A-Z]{3})/i;
        const sheetMatch = sheetCsv.match(plateRegex);

        if (!sheetMatch) {
          throw new Error("No s'ha pogut extreure la matrícula del full de càlcul");
        }

        const sheetPlateText = sheetMatch[1].trim();
        const sheetParts = sheetPlateText.split(/\s+/);
        const sheetPlateNumber = sheetParts[0] || "";
        const sheetPlateLetters = sheetParts[1] || "";

        // Intentar trobar una data al CSV (primer patró DD/MM/YYYY que trobem)
        const dateMatch = sheetCsv.match(/(\d{2}\/\d{2}\/\d{4})/);
        const date = dateMatch?.[1] || new Date().toLocaleDateString("ca-ES");

        // No tenim informació fiable de marca / model al full, així que ho deixem com N/A
        setLastPlate({
          number: sheetPlateNumber,
          letters: sheetPlateLetters,
          date,
          brand: "N/A",
          model: "N/A",
        });
      } catch (err) {
        console.error("Error fetching last plate:", err);
        
        // Fallback to placeholder data
        setLastPlate({
          number: "01**",
          letters: "NJK",
          date: new Date().toLocaleDateString("ca-ES"),
          brand: "VOLKSWAGEN",
          model: "MULTIVAN"
        });
      } finally {
        setLoading(false);
      }
    };

    // Càrrega inicial
    cargarUltimaMatricula();

    // Refresca cada 30 minuts
    const interval = setInterval(cargarUltimaMatricula, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">
          Última matrícula vista
        </h1>
      </div>

      {/* Last Plate Card */}
      {loading ? (
        <div className="bg-card rounded-2xl border-2 border-border p-8 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-secondary rounded w-48 mx-auto mb-4"></div>
            <div className="h-6 bg-secondary rounded w-32 mx-auto"></div>
          </div>
        </div>
      ) : lastPlate ? (
        <div className="bg-gradient-to-br from-secondary to-card rounded-2xl border-2 border-border shadow-lg p-8">
          <div className="text-center">
            <div className="mb-6">
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-foreground text-background rounded-2xl">
                <span className="text-3xl sm:text-4xl font-bold font-mono">
                  {lastPlate.number}
                </span>
                <span className="text-3xl sm:text-4xl font-bold">
                  {lastPlate.letters}
                </span>
              </div>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">
                {lastPlate.date}
              </span>
            </div>
            {(lastPlate.brand !== "N/A" || lastPlate.model !== "N/A") && (
              <div className="pt-4 border-t border-border">
                {lastPlate.brand !== "N/A" && (
                  <p className="text-lg font-bold text-foreground">
                    {lastPlate.brand}
                  </p>
                )}
                {lastPlate.model !== "N/A" && (
                  <p className="text-base text-muted-foreground">
                    {lastPlate.model}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
